import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { uid } from 'uid';
import cx from 'classnames';
import filesize from 'filesize';
import { sprintf } from 'sprintf-js';

import useThemeBlock from '@/hooks/useThemeBlock';
import { LocaleContext } from '@/contexts/locale-context';

import formBlock from '@/components/molecules/Form/block.json';
import block from './block.json';
import { IconDelete, IconDocument, IconInvalid } from './icons';
// styles
import { getStyles } from './styles.css';

/**
 * TYPINGS
 */
type InputFileProps = InputProps &
	ThemeProps & {
		description?: string;
		title?: string;
		nbFiles?: number;
		maxFilesize?: number;
		accept?: string;
	};

interface ForwardedInputFile
	extends React.ForwardRefExoticComponent<
			InputFileProps & React.RefAttributes<HTMLInputElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const InputFile = React.forwardRef<HTMLInputElement, InputFileProps>(
	(
		{
			id = uid(),
			label: title,
			name,
			description,
			fullSize = true,
			accept = '',
			nbFiles = 1,
			maxFilesize = 0,
			required = false,
			disabled = false,
			invalid,
			inputAttributes,
			theme = {},
		},
		ref
	) => {
		const { blockTheme: formBlockTheme } = useThemeBlock(
			formBlock.slug,
			theme
		);

		const innerRef = useRef();
		const { __t } = useContext(LocaleContext);

		const [files, setFiles] = useState([]);

		const isInvalid = invalid && typeof invalid === 'string';
		const isMultiple = nbFiles > 1;
		const hasMaxFiles = files.length >= nbFiles;

		useEffect(() => {
			// Clean state after invalid submission
			if (isInvalid) setFiles([]);
		}, [isInvalid]);

		const rootClass = cx(
			'supt-inputFile',
			'supt-inputWrapper',
			'supt-inputField',
			`supt-inputField--${fullSize ? 'full' : 'half'}`,
			{ 'supt-inputField--error': isInvalid },
			getStyles(formBlockTheme)
		);

		const handleChange = ({ target }) => {
			const newFiles = isMultiple ? [...files] : [];

			[...target.files].forEach((file) => {
				if (newFiles.findIndex(({ name }) => name === file.name) < 0)
					newFiles.push(file);
			});

			if (newFiles.length > nbFiles) {
				newFiles.splice(nbFiles);
			}

			setFiles(newFiles);
		};

		useEffect(() => {
			const dT = new DataTransfer();
			files.forEach((file) => dT.items.add(file));
			// @ts-expect-error
			innerRef.current.querySelector('input').files = dT.files;
		}, [files]);

		const getFilesInfo = (items) =>
			items.map(({ name, size }) => {
				const [filename, basename, ext] = /(.*)\.(.+)$/.exec(name);
				return {
					name: filename,
					basename,
					extension: ext.toUpperCase(),
					size: filesize(size, { round: 0 }),
				};
			});
		const infoFiles = useMemo(() => getFilesInfo(files), [files]);

		const handleClick = (event) => {
			if (!isMultiple && files.length) {
				event.target.value = null;
				setFiles([]);
				event.preventDefault();
			}
		};

		const handleRemove = (index) => {
			const newFiles = [...files];
			newFiles.splice(index, 1);
			setFiles(newFiles);
		};

		let label = __t('input-file-label-add', 'Add a file');

		if (isInvalid) {
			label = isMultiple
				? __t('input-file-label-invalid-multi', 'Add other files')
				: __t('input-file-label-invalid', 'Add another file');
		} else if (hasMaxFiles) {
			label = __t('input-file-label-max-files', 'Maximum reached');
		}

		if (!description || description === '') {
			description = isMultiple
				? sprintf(
						__t('input-file-max-files', 'Maximum %s files.'),
						nbFiles
				  )
				: __t('input-file-max-one-file', '1 file only.');

			if (maxFilesize) {
				description +=
					' ' +
					sprintf(
						isMultiple
							? __t(
									'input-file-multi-max-filesize',
									'Limited to %d MB per file.'
							  )
							: __t(
									'input-file-max-filesize',
									'Limited to %d MB.'
							  ),
						maxFilesize
					);
			}
			if (accept) {
				description +=
					'<br/>' +
					sprintf(
						__t('input-file-accepts', 'Allowed types: %s.'),
						accept.split(',').join(', ')
					);
			}
		}
		const titleHtmlAttr: any = {};
		const descHtmlAttr: any = {
			dangerouslySetInnerHTML: { __html: isInvalid ? null : description },
		};
		if (!isInvalid) {
			titleHtmlAttr['data-optional'] = required
				? ''
				: __t('form-input-optional', 'optional');
		} else {
			descHtmlAttr.role = 'alert';
			descHtmlAttr.dangerouslySetInnerHTML = { __html: invalid };
		}

		return (
			<div className={rootClass} ref={innerRef}>
				<div className="supt-inputFile__inner">
					<div className="supt-inputFile__title-wrap">
						<p
							className="supt-inputFile__title supt-inputField__title"
							{...titleHtmlAttr}
						>
							{title}
						</p>
						{isInvalid ? <IconInvalid /> : <IconDocument />}
					</div>
					{!infoFiles.length || isInvalid ? (
						<p
							className="supt-inputFile__description supt-inputField__description"
							{...descHtmlAttr}
						/>
					) : (
						<ul className="supt-inputFile__list">
							{infoFiles.map((file, index) => (
								<li
									key={index}
									className="supt-inputFile__file"
								>
									<button
										className="supt-inputFile__file__delete"
										onClick={(e) => {
											e.preventDefault();
											handleRemove(index);
										}}
									>
										<span>
											{/* Translators: available variables for the file: `name`, `basename`, `extension`, `size` */}
											{sprintf(
												__t(
													'input-file-file-infos',
													'%(size)s – %(extension)s file – %(name)s'
												),
												file
											)}
										</span>
										<IconDelete />
									</button>
								</li>
							))}
						</ul>
					)}

					<div className="supt-inputFile__labelWrapper supt-inputField__labelWrapper">
						<input
							type="file"
							id={id}
							className="supt-inputFile__input supt-inputField__input"
							name={name}
							multiple={isMultiple}
							disabled={disabled}
							accept={
								accept && accept !== ''
									? accept
											.split(',')
											.map(
												(ext) =>
													ext.includes('/')
														? ext.trim()
														: `.${ext.replace(
																/^[\s\.]*|[\s\.]*$/,
																''
														  )}` // trim whitespaces & `.`
											)
											.join(',')
									: null
							}
							onClick={handleClick}
							onChange={handleChange}
							ref={ref}
							aria-invalid={!!invalid}
							{...inputAttributes}
						/>
						<label
							className={cx(
								'supt-inputFile__label supt-inputField__label',
								{
									'supt-inputFile__label--disabled':
										hasMaxFiles && !isInvalid,
								}
							)}
							htmlFor={id}
						>
							{label}
						</label>
					</div>
				</div>
			</div>
		);
	}
) as ForwardedInputFile;

InputFile.slug = block.slug;
InputFile.title = block.title;
